import React, { useState, useEffect, useRef } from "react";
import "./KitchenPrice.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaEdit, FaTrash } from "react-icons/fa";
import moment from "moment-timezone";

const KitchenPrice = ({ editMode, kitchenData, updateKitchenData }) => {
  // Local state for pricing fields
  const [pricingData, setPricingData] = useState({
    bookingType: kitchenData.bookingType || "hour",
    hourlyRate: kitchenData.hourlyRate || "",
    dayRate: kitchenData.dayRate || "",
    cleaningFee: kitchenData.cleaningFee || "",
    minBooking: kitchenData.minBooking || "",
    securityDeposit: kitchenData.securityDeposit || "",
    rateEntries: kitchenData.rateEntries || [],
    blockedDates: kitchenData.blockedDates || [],
  });

  const initializedRef = useRef(false);

  // Initialize local state from parent's kitchenData in edit mode
  useEffect(() => {
    if (editMode && !initializedRef.current && kitchenData.KitchenID) {
      setPricingData({
        bookingType: kitchenData.bookingType || "hour",
        hourlyRate: kitchenData.hourlyRate || "",
        dayRate: kitchenData.dayRate || "",
        cleaningFee: kitchenData.cleaningFee || "",
        minBooking: kitchenData.minBooking || "",
        securityDeposit: kitchenData.securityDeposit || "",
        rateEntries: kitchenData.rateEntries || [],
        blockedDates: kitchenData.blockedDates || [],
      });
      initializedRef.current = true;
    }
  }, [editMode, kitchenData]);

  // Push local pricingData changes up to the parent's state
  useEffect(() => {
    updateKitchenData({ ...pricingData });
  }, [pricingData, updateKitchenData]);

  // Local state for managing new availability slot
  const [slotData, setSlotData] = useState({
    newStartDate: null,
    newEndDate: null,
    newAvailabilityPrice: "",
    newRecurring: false,
    editingSlotIndex: null,
  });

  // Check if the new slot overlaps existing slots
  const isAvailabilityOverlap = (newSlot, excludeIndex = null) => {
    const newStart = new Date(newSlot.startDateTime).getTime();
    const newEnd = new Date(newSlot.endDateTime).getTime();
    return pricingData.rateEntries.some((slot, index) => {
      if (index === excludeIndex) return false;
      const slotStart = new Date(slot.startDateTime).getTime();
      const slotEnd = new Date(slot.endDateTime).getTime();
      return newStart < slotEnd && newEnd > slotStart;
    });
  };

  // Handler for adding/updating an availability slot
  const handleAddOrUpdateSlot = () => {
    const {
      newStartDate,
      newEndDate,
      newAvailabilityPrice,
      editingSlotIndex,
      newRecurring,
    } = slotData;

    if (!newStartDate || !newEndDate || !newAvailabilityPrice) {
      alert("Please select start/end times and a price.");
      return;
    }
    if (newEndDate <= newStartDate) {
      alert("End date/time must be after start date/time.");
      return;
    }
    const price = parseFloat(newAvailabilityPrice);
    if (isNaN(price) || price <= 0) {
      alert("Price must be a number greater than 0.");
      return;
    }

    // Convert times to UTC based on the kitchen's timezone using moment-timezone.
    const timeZone =
      kitchenData.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startUtc = moment.tz(newStartDate, timeZone).utc().toISOString();
    const endUtc = moment.tz(newEndDate, timeZone).utc().toISOString();

    const slot = {
      startDateTime: startUtc,
      endDateTime: endUtc,
      price,
      recurring: newRecurring,
    };

    if (newRecurring) {
      // Store both start and end weekday names.
      const startDay = newStartDate.toLocaleDateString("en-US", {
        weekday: "long",
      });
      const endDay = newEndDate.toLocaleDateString("en-US", {
        weekday: "long",
      });
      slot.startDay = startDay;
      slot.endDay = endDay;
      // Calculate duration in milliseconds (used internally)
      slot.duration = newEndDate.getTime() - newStartDate.getTime();
    }

    if (editingSlotIndex !== null) {
      if (isAvailabilityOverlap(slot, editingSlotIndex)) {
        alert("The updated time slot overlaps with another slot.");
        return;
      }
      const updatedEntries = [...pricingData.rateEntries];
      updatedEntries[editingSlotIndex] = slot;
      setPricingData({ ...pricingData, rateEntries: updatedEntries });
      setSlotData({
        newStartDate: null,
        newEndDate: null,
        newAvailabilityPrice: "",
        newRecurring: false,
        editingSlotIndex: null,
      });
    } else {
      if (isAvailabilityOverlap(slot)) {
        alert("This time slot overlaps with an existing slot.");
        return;
      }
      setPricingData({
        ...pricingData,
        rateEntries: [...pricingData.rateEntries, slot],
      });
      setSlotData({
        newStartDate: null,
        newEndDate: null,
        newAvailabilityPrice: "",
        newRecurring: false,
        editingSlotIndex: null,
      });
    }
  };

  const handleEditSlot = (index) => {
    const slot = pricingData.rateEntries[index];
    setSlotData({
      newStartDate: new Date(slot.startDateTime),
      newEndDate: new Date(slot.endDateTime),
      newAvailabilityPrice: slot.price.toString(),
      newRecurring: slot.recurring,
      editingSlotIndex: index,
    });
  };

  const handleRemoveSlot = (index) => {
    const updatedEntries = pricingData.rateEntries.filter(
      (_, i) => i !== index
    );
    setPricingData({ ...pricingData, rateEntries: updatedEntries });
    if (slotData.editingSlotIndex === index) {
      setSlotData({
        newStartDate: null,
        newEndDate: null,
        newAvailabilityPrice: "",
        newRecurring: false,
        editingSlotIndex: null,
      });
    }
  };

  const handleCancelEdit = () => {
    setSlotData({
      newStartDate: null,
      newEndDate: null,
      newAvailabilityPrice: "",
      newRecurring: false,
      editingSlotIndex: null,
    });
  };

  const handleChange = (field, value) => {
    setPricingData({ ...pricingData, [field]: value });
  };

  return (
    <div className="kitchen-price">
      <div className="price-group">
        {pricingData.bookingType === "hour" ? (
          <div className="price-form-group">
            <label>Hourly Rate</label>
            <input
              type="number"
              placeholder="Enter hourly rate"
              value={pricingData.hourlyRate}
              onChange={(e) => handleChange("hourlyRate", e.target.value)}
            />
          </div>
        ) : (
          <div className="price-form-group">
            <label>Day Rate</label>
            <input
              type="number"
              placeholder="Enter day rate"
              value={pricingData.dayRate}
              onChange={(e) => handleChange("dayRate", e.target.value)}
            />
          </div>
        )}
        <div className="price-form-group">
          {pricingData.bookingType === "hour" ? (
            <>
              <label>Minimum Booking Hours</label>
              <input
                type="number"
                placeholder="Enter minimum hours"
                value={pricingData.minBooking}
                onChange={(e) => handleChange("minBooking", e.target.value)}
              />
              <small className="field-note">
                Note: We recommend a minimum of 3 hours.
              </small>
            </>
          ) : (
            <>
              <label>Minimum Booking Days</label>
              <input
                type="number"
                placeholder="Enter minimum days"
                value={pricingData.minBooking}
                onChange={(e) => handleChange("minBooking", e.target.value)}
              />
              <small className="field-note">
                Note: We recommend a minimum of 1 day.
              </small>
            </>
          )}
        </div>
      </div>
      <div className="form-row">
        <div className="price-form-group">
          <label>Cleaning Fees ($)</label>
          <input
            type="number"
            placeholder="Enter cleaning fee"
            value={pricingData.cleaningFee}
            onChange={(e) => handleChange("cleaningFee", e.target.value)}
          />
        </div>
        <div className="price-form-group">
          <label>Security Deposit ($)</label>
          <input
            type="number"
            placeholder="Enter security deposit"
            value={pricingData.securityDeposit}
            onChange={(e) => handleChange("securityDeposit", e.target.value)}
          />
          <small className="field-note">
            Note: Adjust this for longer bookings if necessary.
          </small>
        </div>
      </div>
      <h2 className="price-title">
        {pricingData.bookingType === "hour"
          ? "Set Hourly Availability"
          : "Set Daily Availability"}
      </h2>
      <small className="info-note">
        Specify when your kitchen is available. For recurring weekly
        availability, check "Set weekly" to indicate the slot repeats every
        week.
      </small>
      <div className="availability-section">
        <div className="availability-row">
          <div className="price-form-group">
            <label>Start Time</label>
            <DatePicker
              selected={slotData.newStartDate}
              onChange={(date) =>
                setSlotData({ ...slotData, newStartDate: date })
              }
              popperPlacement="bottom-start"
              portalId="root" /* Fixes positioning issues */
              wrapperClassName="date-picker-wrapper" /* Add a wrapper class for better control */
              shouldCloseOnScroll={false} /* Prevents closing when scrolling */
              showTimeSelect={pricingData.bookingType === "hour"}
              dateFormat={pricingData.bookingType === "hour" ? "Pp" : "P"}
              placeholderText="Select start"
            />
          </div>
          <div className="price-form-group">
            <label>End Time</label>
            <DatePicker
              selected={slotData.newEndDate}
              onChange={(date) =>
                setSlotData({ ...slotData, newEndDate: date })
              }
              showTimeSelect={pricingData.bookingType === "hour"}
              dateFormat={pricingData.bookingType === "hour" ? "Pp" : "P"}
              placeholderText="Select end"
              popperPlacement="bottom-start"
              portalId="root" /* Fixes positioning issues */
              wrapperClassName="date-picker-wrapper" /* Add a wrapper class for better control */
              // shouldCloseOnScroll={false} /* Prevents closing when scrolling */
            />
          </div>
          <div className="price-form-group">
            <label>Rate</label>
            <input
              type="number"
              placeholder="Enter price"
              value={slotData.newAvailabilityPrice}
              onChange={(e) =>
                setSlotData({
                  ...slotData,
                  newAvailabilityPrice: e.target.value,
                })
              }
            />
          </div>
          <div className="price-form-group recurring-group">
            <label>Set weekly</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={slotData.newRecurring}
                onChange={(e) =>
                  setSlotData({ ...slotData, newRecurring: e.target.checked })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="entry-btn-container">
            <button
              type="button"
              className="add-entry-btn"
              onClick={handleAddOrUpdateSlot}
            >
              {slotData.editingSlotIndex !== null ? "Update" : "Add"}
            </button>
            {slotData.editingSlotIndex !== null && (
              <button
                type="button"
                className="add-entry-btn cancel-edit-btn"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
        {pricingData.rateEntries.length > 0 && (
          <div className="availability-list">
            <h3>Availability Slots</h3>
            <ul>
              {pricingData.rateEntries.map((slot, index) => (
                <li key={index}>
                  <span>
                    {slot.recurring ? (
                      <>
                        Every week from {slot.startDay}{" "}
                        {new Date(slot.startDateTime).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}{" "}
                        to {slot.endDay}{" "}
                        {new Date(slot.endDateTime).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}{" "}
                        at ${slot.price}
                      </>
                    ) : (
                      <>
                        {new Date(slot.startDateTime).toLocaleString()} –{" "}
                        {new Date(slot.endDateTime).toLocaleString()} : $
                        {slot.price}
                      </>
                    )}
                  </span>
                  <div className="availability-actions">
                    <FaEdit
                      className="slot-edit-icon"
                      onClick={() => handleEditSlot(index)}
                    />
                    <FaTrash
                      className="slot-delete-icon"
                      onClick={() => handleRemoveSlot(index)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default KitchenPrice;
